import FormField from "../../components/FormField";
import TextInput from "../../components/TextInput";
import Select from "../../components/Select";
import { useLieuxList } from "../../../hooks/useLieuxList";
import type { ProgrammeDto } from "../../../api/programmesApi";

const TAG_OPTIONS = [
  { value: "", label: "—" },
  { value: "vernissage", label: "Vernissage / ouverture" },
  { value: "performance", label: "Performance" },
  { value: "atelier", label: "Atelier" },
  { value: "concert", label: "Concert" },
  { value: "visite", label: "Visite guidée" },
  { value: "libre", label: "Visite libre" },
  { value: "démo", label: "Démonstration" },
  { value: "marché", label: "Marché" },
  { value: "talk", label: "Conversation / talk" },
  { value: "convivial", label: "Moment convivial" },
];

interface Props {
  value: ProgrammeDto;
  onChange: (next: ProgrammeDto) => void;
}

export default function ProgrammeFormFields({ value, onChange }: Props) {
  const set = <K extends keyof ProgrammeDto>(key: K, v: ProgrammeDto[K]) => onChange({ ...value, [key]: v });
  const lieuxQ = useLieuxList({ page: 0, size: 200, sort: "name,asc" });

  const lieuxOptions = lieuxQ.data?.content ?? [];
  const usingLieu = Boolean(value.lieuId);

  return (
    <div className="bo-stack">
      <FormField label="Titre" required>
        <TextInput value={value.title ?? ""} onChange={(e) => set("title", e.target.value)} required />
      </FormField>
      <FormField label="Sous-titre">
        <TextInput value={value.subtitle ?? ""} onChange={(e) => set("subtitle", e.target.value)} />
      </FormField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--sp-4)" }}>
        <fieldset className="bo-fieldset" style={{ margin: 0 }}>
          <legend>Début</legend>
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: "var(--sp-3)" }}>
            <FormField label="Date">
              <TextInput
                type="date"
                value={value.date ?? ""}
                onChange={(e) => set("date", e.target.value || undefined)}
              />
            </FormField>
            <FormField label="Heure">
              <TextInput
                type="time"
                value={value.time ?? ""}
                onChange={(e) => set("time", e.target.value || undefined)}
              />
            </FormField>
          </div>
        </fieldset>

        <fieldset className="bo-fieldset" style={{ margin: 0 }}>
          <legend>Fin</legend>
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: "var(--sp-3)" }}>
            <FormField label="Date" hint="Optionnel — vide ⇒ même jour">
              <TextInput
                type="date"
                min={value.date ?? undefined}
                value={value.endDate ?? ""}
                onChange={(e) => set("endDate", e.target.value || null)}
              />
            </FormField>
            <FormField label="Heure" hint="Optionnel">
              <TextInput
                type="time"
                value={value.endTime ?? ""}
                onChange={(e) => set("endTime", e.target.value || null)}
              />
            </FormField>
          </div>
        </fieldset>
      </div>
      <div className="bo-grid-2">
        <FormField label="Date de fin" hint="Laissez vide si le programme se termine le même jour.">
          <TextInput
            type="date"
            value={value.endDate ?? ""}
            min={value.date ?? undefined}
            onChange={(e) => set("endDate", e.target.value || undefined)}
          />
        </FormField>
        <FormField label="Heure de fin">
          <TextInput
            type="time"
            value={value.endTime ?? ""}
            onChange={(e) => set("endTime", e.target.value || undefined)}
          />
        </FormField>
      </div>

      <FormField
        label="Lieu du catalogue"
        hint="Choisissez un lieu existant, ou laissez vide pour saisir une adresse libre ci-dessous."
      >
        <Select
          value={value.lieuId ?? ""}
          onChange={(e) => set("lieuId", e.target.value || undefined)}
          disabled={lieuxQ.isLoading}
        >
          <option value="">— Adresse libre —</option>
          {lieuxOptions.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
              {l.address?.city ? ` · ${l.address.city}` : ""}
            </option>
          ))}
        </Select>
      </FormField>

      <FormField
        label="Nom de l'adresse (si pas de lieu sélectionné)"
        hint={usingLieu ? "Ignoré car un lieu du catalogue est sélectionné." : "Ex. « Salle communale », « Place du marché »"}
      >
        <TextInput
          value={value.addressName ?? ""}
          onChange={(e) => set("addressName", e.target.value)}
          disabled={usingLieu}
          placeholder={usingLieu ? "—" : "Salle communale, Mons centre…"}
        />
      </FormField>

      <FormField label="Image (URL)">
        <TextInput value={value.imageLink ?? ""} onChange={(e) => set("imageLink", e.target.value)} />
      </FormField>
      <FormField label="Tarif (optionnel)">
        <TextInput
          type="number"
          step="0.01"
          value={value.price?.toString() ?? ""}
          onChange={(e) => set("price", e.target.value === "" ? undefined : Number(e.target.value))}
        />
      </FormField>
      <FormField label="Catégorie" hint="Détermine l'icône et la couleur affichées dans le programme public">
        <Select value={value.tag ?? ""} onChange={(e) => set("tag", e.target.value || undefined)}>
          {TAG_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </FormField>
    </div>
  );
}
